import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SanitizedEmail } from './emailHtml';
import { splitQuotedContent, type ParsedEmailContent } from './quotedContent';

/**
 * Vendor mail rendered the way the mail app renders it: a sandboxed iframe per
 * body part, sized to its own content, with the quoted tail collapsed behind a
 * toggle. Lifted from `features/email-detail/components/MessageBody` minus the
 * keyboard-event forwarding — this app has no global single-key shortcuts for
 * an in-iframe keystroke to collide with.
 */

interface MessageBodyProps {
  sanitizedHtml: SanitizedEmail | null;
  minHeight?: string;
  contentWrapperClassName?: string;
  expandQuotedByDefault?: boolean;
}

const DEFAULT_MIN_HEIGHT = 'min-h-[100px]';

const BASE_IFRAME_STYLES = `
  html, body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #1a1a1a;
    word-wrap: break-word;
    overflow-wrap: break-word;
    -webkit-text-size-adjust: 100%;
  }
  /* Height follows the content (EmailIframe), so nothing scrolls vertically;
     a fixed-width vendor layout (<table width="600">) scrolls sideways on a
     narrow screen instead of being cut off. */
  body {
    overflow-x: auto;
    overflow-y: hidden;
  }
  pre {
    white-space: pre-wrap;
    overflow-x: auto;
  }
  a {
    color: #1a73e8;
  }
  @media (prefers-color-scheme: dark) {
    html, body {
      background: transparent;
      color: #e0e0e0;
    }
    a {
      color: #8ab4f8;
    }
  }
`;

function buildSrcdoc(styles: string, body: string, extraStyles?: string): string {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>${BASE_IFRAME_STYLES}</style>
${extraStyles ? `<style>${extraStyles}</style>` : ''}
${styles}
</head>
<body>${body}</body>
</html>`;
}

function EmailIframe({ styles, body, dimmed }: { styles: string; body: string; dimmed?: boolean }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(100);
  const observerRef = useRef<ResizeObserver | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    const doc = iframe.contentDocument.documentElement;
    // A sideways scrollbar, when the platform draws one, takes its height
    // from the viewport; add it back so the last line isn't hidden under it.
    const scrollbar = (iframe.contentWindow?.innerHeight ?? doc.clientHeight) - doc.clientHeight;
    const newHeight = doc.scrollHeight + Math.max(0, scrollbar);
    if (newHeight > 0) {
      setHeight(newHeight);
    }
  }, []);

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;

    // Measure after layout; re-measure shortly after in case images or fonts
    // were not ready the first time.
    requestAnimationFrame(() => {
      updateHeight();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(updateHeight, 150);
    });

    observerRef.current?.disconnect();
    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(iframe.contentDocument.body);
    observerRef.current = observer;
  }, [updateHeight]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const extraStyles = dimmed ? 'body { opacity: 0.6; }' : undefined;
  const srcdoc = useMemo(() => buildSrcdoc(styles, body, extraStyles), [styles, body, extraStyles]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={handleLoad}
      style={{
        border: 'none',
        width: '100%',
        height: `${height}px`,
        display: 'block',
        overflow: 'hidden',
        colorScheme: 'light dark',
      }}
      title="Email content"
    />
  );
}

export function MessageBody({
  sanitizedHtml,
  minHeight = DEFAULT_MIN_HEIGHT,
  contentWrapperClassName,
  expandQuotedByDefault = false,
}: MessageBodyProps) {
  const parsedContent = useMemo<ParsedEmailContent>(() => {
    if (!sanitizedHtml || !sanitizedHtml.body.trim()) {
      return {
        mainContent: { styles: '', body: '' },
        quotedContent: null,
        hasQuotedContent: false,
        mainIsEmpty: false,
      };
    }
    return splitQuotedContent(sanitizedHtml);
  }, [sanitizedHtml]);

  // A reply with no new text is 100% quote — start expanded so the message
  // doesn't render blank behind a "Show quoted text" button.
  const defaultExpanded = expandQuotedByDefault || parsedContent.mainIsEmpty;
  const [showQuoted, setShowQuoted] = useState(defaultExpanded);

  useEffect(() => {
    setShowQuoted(defaultExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedContent]);

  if (!sanitizedHtml || !sanitizedHtml.body.trim()) {
    return null;
  }

  return (
    <section className={cn(minHeight, 'space-y-3')}>
      <div className={cn('w-full', contentWrapperClassName)}>
        <EmailIframe styles={parsedContent.mainContent.styles} body={parsedContent.mainContent.body} />

        {parsedContent.hasQuotedContent && (
          <div className="mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQuoted(!showQuoted)}
              className="gap-2 text-xs text-muted-foreground"
            >
              {showQuoted ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  Hide quoted text
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />
                  Show quoted text
                </>
              )}
            </Button>

            {showQuoted && parsedContent.quotedContent && (
              <div className="mt-2 border-l-2 border-muted pl-4">
                <EmailIframe
                  styles={parsedContent.quotedContent.styles}
                  body={parsedContent.quotedContent.body}
                  dimmed
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
